import { PkDropdown, PkButton, PkIcon, PkMenu, PkMenuItem } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import objectHash from 'object-hash';
import type { ComponentType } from 'react';
import { useState, useMemo, memo, useEffect } from 'react';

import type { PkTheme } from '@shared-types/common.types';

import * as styles from './styles.module.scss';

export type PanenkaDropdownProps<T> = {
  elements: T[];
  dropdownClassName?: string;
  buttonClassName?: string;
  buttonWidth?: string;
  buttonTheme?: PkTheme | 'inherit';
  menuTheme?: PkTheme | 'inherit';
  menuClassName?: string;
  menuItemClassName?: string;
  selectedItem?: T;
  onSelect: (element: T, index?: number) => unknown;
  ButtonContent: ComponentType<{ element: T }>;
  MenuItemContent: ComponentType<{ element: T; selected: boolean; index: number }>;
  multipleDropdowns?: boolean;
  sameWidthDropdown?: boolean;
  highContrastButton?: boolean;
  circleIcon?: boolean;
  iconColor?: string;
  hideArrow?: boolean;
  hideSectionDivider?: boolean;
  dropdownDistance?: number;
};

const PanenkaDropdown = <T,>({
  elements,
  dropdownClassName,
  buttonClassName,
  buttonWidth,
  buttonTheme = 'dark',
  menuTheme = 'light',
  menuClassName,
  menuItemClassName,
  selectedItem,
  onSelect,
  ButtonContent,
  MenuItemContent,
  multipleDropdowns,
  sameWidthDropdown = true,
  highContrastButton = true,
  circleIcon = true,
  iconColor = 'interaction',
  hideArrow = true,
  hideSectionDivider = true,
  dropdownDistance = 12,
}: PanenkaDropdownProps<T>) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<number>(null);

  useEffect(() => {
    if (selectedItem) {
      const index = elements?.findIndex((e) => e === selectedItem);
      setSelected(index);
    }
  }, [elements, selectedItem]);

  const handleOpen = (status: boolean) => () => {
    setOpen(status);
  };

  const handleSelect = (element: T, i: number) => () => {
    setSelected(i);
    setOpen(false);
    onSelect && onSelect(element, i);
  };

  const icon = useMemo(
    () => `navigation-chevron-${open ? 'up' : 'down'}${circleIcon ? '-circle' : ''}`,
    [circleIcon, open],
  );
  const disabled = useMemo(() => !elements || elements.length <= 1, [elements]);

  return (
    <PkDropdown
      className={clsx(styles.pkDropdown, multipleDropdowns && styles.multiple, dropdownClassName)}
      disabled={disabled ? true : undefined}
      hideArrow={hideArrow}
      distance={dropdownDistance}
      onPkShow={handleOpen(true)}
      onPkHide={handleOpen(false)}
      sameWidth={sameWidthDropdown}
    >
      <PkButton
        className={clsx(styles.pkButton, buttonClassName)}
        disabled={disabled ? true : undefined}
        appearance='outlined'
        highContrast={highContrastButton}
        width={buttonWidth}
        slot='trigger'
        pk-theme={buttonTheme}
      >
        <ButtonContent element={elements?.[selected]} />
        <PkIcon name={icon} color={iconColor} className='pk-ml--xs pk-align-self--end' />
      </PkButton>
      {!!elements?.length && (
        <PkMenu
          className={clsx(styles.pkMenu, menuClassName)}
          hideSectionDivider={hideSectionDivider}
          pk-theme={menuTheme}
        >
          {elements.map((element, i) => (
            <PkMenuItem
              className={clsx(styles.pkMenuItem, menuItemClassName)}
              key={`menu-item-${element ? objectHash(element as any) : i}`}
              onClick={handleSelect(element, i)}
              selected={selected === i}
            >
              <MenuItemContent element={element} selected={selected === i} index={i} />
              {selected === i && <PkIcon name='actions-check-circle' color='support-01' slot='suffix' />}
            </PkMenuItem>
          ))}
        </PkMenu>
      )}
    </PkDropdown>
  );
};

export default memo(PanenkaDropdown) as typeof PanenkaDropdown;
